import React, { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import Editor from '@monaco-editor/react'
import { 
  FileText, 
  Code, 
  Copy, 
  Download, 
  Clock,
  Database,
  CheckCircle,
  XCircle,
  AlertCircle,
  ChevronDown,
  ChevronRight,
  Sparkles
} from 'lucide-react'
import { useAPIResponseStore, useAPIRequestStore } from '../../stores/apiStore'
import { APIService } from '../../services/apiService'
import { useTheme } from '../../hooks/useTheme'
import { useToast } from '../ui/ToastProvider'
import EmptyStateIllustration from '../ui/EmptyStateIllustration'
import APIInfoCards from './APIInfoCards'
import AIPromptModal from './AIPromptModal'

const MAX_EXPAND_DEPTH = 4
const MAX_ARRAY_ITEMS_TO_EXPAND = 10

function collectPathsToExpand(
  obj: any,
  path: string,
  depth: number,
  maxDepth: number,
  result: Set<string>
): void {
  if (depth >= maxDepth) return
  if (typeof obj !== 'object' || obj === null) return
  if (Array.isArray(obj)) {
    obj.slice(0, MAX_ARRAY_ITEMS_TO_EXPAND).forEach((item, i) => {
      collectPathsToExpand(item, `${path}[${i}]`, depth + 1, maxDepth, result)
    })
  } else {
    Object.keys(obj).forEach((key) => {
      const nodePath = `${path}.${key}`
      result.add(nodePath)
      collectPathsToExpand(obj[key], nodePath, depth + 1, maxDepth, result)
    })
  }
}

const ResponsePanel: React.FC = () => {
  const { currentResponse, activeView, setActiveView } = useAPIResponseStore()
  const { selectedEndpoint, baseUrl, headers, requestBody, queryParams } = useAPIRequestStore()
  const [expandedHeaders, setExpandedHeaders] = useState(false)
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set())
  const [isAIPromptOpen, setIsAIPromptOpen] = useState(false)
  const isDark = useTheme()
  const { showSuccess, showError } = useToast()

  const apiService = APIService.getInstance()

  // Expand JSON tree recursively up to max depth when response changes
  React.useEffect(() => {
    if (currentResponse?.data && typeof currentResponse.data === 'object' && currentResponse.data !== null) {
      const pathsToExpand = new Set<string>()
      collectPathsToExpand(
        currentResponse.data,
        '',
        0,
        MAX_EXPAND_DEPTH,
        pathsToExpand
      )
      setExpandedNodes(pathsToExpand)
    }
  }, [currentResponse?.data])

  const handleCopyResponse = async () => {
    if (currentResponse?.data) {
      try {
        await navigator.clipboard.writeText(JSON.stringify(currentResponse.data, null, 2))
        showSuccess('Response copied to clipboard!')
      } catch (error) {
        showError('Failed to copy response')
      }
    } else {
      showError('No response data to copy')
    }
  }

  const handleDownloadResponse = () => {
    if (currentResponse?.data) {
      const blob = new Blob([JSON.stringify(currentResponse.data, null, 2)], { 
        type: 'application/json' 
      })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `response-${currentResponse.id}.json`
      a.click()
      URL.revokeObjectURL(url)
    }
  }

  const getStatusIcon = (status: number) => {
    if (status >= 200 && status < 300) {
      return <CheckCircle className="w-5 h-5 text-green-500" />
    } else if (status >= 300 && status < 400) {
      return <AlertCircle className="w-5 h-5 text-yellow-500" />
    } else if (status >= 400) {
      return <XCircle className="w-5 h-5 text-red-500" />
    }
    return <AlertCircle className="w-5 h-5 text-gray-500" />
  }

  const renderJsonView = (data: any, path: string = '') => {
    if (typeof data === 'object' && data !== null) {
      if (Array.isArray(data)) {
        return (
          <div className="space-y-1">
            {data.map((item, index) => (
              <div key={index} className="ml-4">
                <div className="flex items-center space-x-2">
                  <span className="text-blue-600 dark:text-blue-400 font-medium">
                    [{index}]
                  </span>
                  <span className="text-gray-600 dark:text-gray-400">:</span>
                </div>
                <div className="ml-4">
                  {renderJsonView(item, `${path}[${index}]`)}
                </div>
              </div>
            ))}
          </div>
        )
      } else {
        return (
          <div className="space-y-1">
            {Object.entries(data).map(([key, value]) => {
              const nodePath = `${path}.${key}`
              const isExpanded = expandedNodes.has(nodePath)
              const isExpandable = typeof value === 'object' && value !== null
              
              return (
                <div key={key} className="ml-4">
                  <div className="flex items-center space-x-2">
                    {isExpandable && (
                      <button
                        onClick={() => {
                          const newExpanded = new Set(expandedNodes)
                          if (isExpanded) {
                            newExpanded.delete(nodePath)
                          } else {
                            newExpanded.add(nodePath)
                          }
                          setExpandedNodes(newExpanded)
                        }}
                        className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                      >
                        {isExpanded ? (
                          <ChevronDown className="w-3 h-3" />
                        ) : (
                          <ChevronRight className="w-3 h-3" />
                        )}
                      </button>
                    )}
                    <span className="text-blue-600 dark:text-blue-400 font-medium">
                      {key}
                    </span>
                    <span className="text-gray-600 dark:text-gray-400">:</span>
                    {!isExpandable && (
                      <span className="text-gray-900 dark:text-gray-100">
                        {typeof value === 'string' ? `"${value}"` : String(value)}
                      </span>
                    )}
                  </div>
                  {isExpandable && (isExpanded || path === '') && (
                    <div className="ml-4">
                      {renderJsonView(value, nodePath)}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )
      }
    }
    
    return (
      <span className="text-gray-900 dark:text-gray-100">
        {typeof data === 'string' ? `"${data}"` : String(data)}
      </span>
    )
  }

  const renderRawView = () => {
    if (!currentResponse?.data) return null
    
    return (
      <div className="w-full h-full border border-gray-300 dark:border-gray-600 rounded-md">
        <Editor
          height="100%"
          language="json"
          theme={isDark ? "vs-dark" : "light"}
          value={JSON.stringify(currentResponse.data, null, 2)}
          options={{
            minimap: { enabled: false },
            lineNumbers: 'on',
            readOnly: true,
            fontSize: 14,
            wordWrap: 'off',
            automaticLayout: true,
            scrollBeyondLastLine: true,
            folding: true,
            bracketPairColorization: { enabled: true },
            renderWhitespace: 'boundary',
            overviewRulerBorder: false,
            hideCursorInOverviewRuler: true,
            overviewRulerLanes: 0,
            glyphMargin: false,
            lineDecorationsWidth: 0,
            lineNumbersMinChars: 3,
            renderLineHighlight: 'none',
            fixedOverflowWidgets: true,
            padding: { top: 10, bottom: 10 }
          }}
          onMount={(editor) => {
            // Force layout recalculation with proper timing
            setTimeout(() => {
              editor.layout();
              editor.revealLine(1);
              editor.getAction?.('editor.unfoldAll')?.run();
            }, 200);
            
            // Add resize listener to handle dynamic resizing
            const resizeObserver = new ResizeObserver(() => {
              editor.layout();
            });
            
            const container = editor.getContainerDomNode().parentElement;
            if (container) {
              resizeObserver.observe(container);
            }
          }}
        />
      </div>
    )
  }

  if (!currentResponse) {
    // Show API info cards when no endpoint is selected
    if (!selectedEndpoint) {
      return <APIInfoCards />
    }
    
    // Show empty state when endpoint is selected but no response yet
    return (
      <div className="flex flex-col h-full">
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            Response
          </h2>
        </div>
        <div className="flex-1 flex items-center justify-center">
          <EmptyStateIllustration
            type="response"
            title="Ready to explore"
            description="Send a request to see the API response here. The response will include status, headers, and formatted data."
          />
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="p-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between mb-4">
          <div className="flex flex-col">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              Response
            </h2>
            {currentResponse?.endpoint && (
              <div className="flex items-center space-x-2 mt-1">
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  Response for:
                </span>
                <span className="px-2 py-1 bg-primary-100 dark:bg-primary-900 text-primary-800 dark:text-primary-200 rounded-md text-sm font-medium">
                  {currentResponse.endpoint.method.toUpperCase()} {currentResponse.endpoint.name}
                </span>
              </div>
            )}
          </div>
          <div className="flex items-center space-x-2">
            {currentResponse && currentResponse.status >= 200 && currentResponse.status < 300 && selectedEndpoint && (
              <button
                onClick={() => setIsAIPromptOpen(true)}
                className="flex items-center space-x-1 px-3 py-1.5 bg-primary-100 dark:bg-primary-900 text-primary-800 dark:text-primary-200 rounded-md text-sm font-medium hover:bg-primary-200 dark:hover:bg-primary-800 transition-colors"
                title="Generate AI Prompt"
              >
                <Sparkles className="w-4 h-4" />
                <span>Prompt for AI</span>
              </button>
            )}
            <button
              onClick={handleCopyResponse}
              className="p-2 text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white transition-colors"
              title="Copy Response"
            >
              <Copy className="w-4 h-4" />
            </button>
            <button
              onClick={handleDownloadResponse}
              className="p-2 text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white transition-colors"
              title="Download Response"
            >
              <Download className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Status and Metrics */}
        <div className="flex items-center space-x-6 mb-4">
          <div className="flex items-center space-x-2">
            {getStatusIcon(currentResponse.status)}
            <span className={`font-medium ${apiService.getStatusColor(currentResponse.status)}`}>
              {currentResponse.status} {currentResponse.statusText}
            </span>
          </div>
          <div className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400">
            <Clock className="w-4 h-4" />
            <span>{apiService.formatResponseTime(currentResponse.responseTime)}</span>
          </div>
          <div className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400">
            <Database className="w-4 h-4" />
            <span>{apiService.formatResponseSize(currentResponse.size)}</span>
          </div>
        </div>

        {/* Headers */}
        <div className="mb-4">
          <button
            onClick={() => setExpandedHeaders(!expandedHeaders)}
            className="flex items-center space-x-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors"
          >
            {expandedHeaders ? (
              <ChevronDown className="w-4 h-4" />
            ) : (
              <ChevronRight className="w-4 h-4" />
            )}
            <span>Headers ({Object.keys(currentResponse.headers).length})</span>
          </button>
          <AnimatePresence>
            {expandedHeaders && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="mt-2 bg-gray-50 dark:bg-gray-700 rounded-md p-3"
              >
                {Object.entries(currentResponse.headers).map(([key, value]) => (
                  <div key={key} className="flex justify-between text-sm">
                    <span className="font-medium text-gray-700 dark:text-gray-300">{key}:</span>
                    <span className="text-gray-600 dark:text-gray-400">{value}</span>
                  </div>
                ))}
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* View Toggle */}
        <div className="flex bg-gray-100 dark:bg-gray-700 rounded-md p-1">
          <button
            onClick={() => setActiveView('json')}
            className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
              activeView === 'json'
                ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
            }`}
          >
            <Code className="w-4 h-4 inline mr-1" />
            JSON
          </button>
          <button
            onClick={() => setActiveView('raw')}
            className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
              activeView === 'raw'
                ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
            }`}
          >
            <FileText className="w-4 h-4 inline mr-1" />
            Raw
          </button>
        </div>
      </div>

      {/* Response Content */}
      <div className="flex-1 min-h-0 p-4 overflow-hidden">
        <motion.div
          key={activeView}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="w-full h-full min-h-0"
        >
          {activeView === 'json' && (
            <div className="h-full overflow-auto border border-gray-300 dark:border-gray-600 rounded-md p-4">
              {renderJsonView(currentResponse.data)}
            </div>
          )}
          {activeView === 'raw' && renderRawView()}
        </motion.div>
      </div>

      {/* AI Prompt Modal */}
      <AIPromptModal
        isOpen={isAIPromptOpen}
        onClose={() => setIsAIPromptOpen(false)}
        request={{
          endpoint: selectedEndpoint!,
          baseUrl,
          headers,
          requestBody,
          queryParams
        }}
        response={currentResponse}
      />
    </div>
  )
}

export default ResponsePanel
